/**
 * usePermissions Hook
 * 
 * Provides permission checking capabilities for pilets and shell components.
 * Can be used directly in pilets by importing from 'oneportal-shell'.
 */

import { useAuth } from '../contexts/AuthContext';
import type { ModuleId, ModuleAction } from '../services/permissionService';
import type { ApiResource, ApiAction } from '../types';

export interface UsePermissionsReturn {
  /**
   * Current user object
   */
  user: any | null;

  /**
   * Active role selected by user
   */
  activeRole: string | null;

  /**
   * Check legacy format permission: "module.{moduleId}.{action}"
   */
  hasPermission: (permission: string) => boolean;

  /**
   * Check module permission (OnePortal format)
   */
  hasModulePermission: (moduleId: ModuleId | string, action: ModuleAction | string) => boolean;

  /**
   * Check resource permission (API format)
   */
  hasResourcePermission: (resource: ApiResource | string, action: ApiAction | string) => boolean;

  /**
   * Check if user can access a module (has 'view' permission)
   */
  canAccessModule: (moduleId: ModuleId | string) => boolean;

  /**
   * Get all accessible modules for current role
   */
  getAccessibleModules: () => (ModuleId | string)[];

  /**
   * Loading state while permissions are being fetched
   */
  isLoadingPermissions: boolean;

  /**
   * Refreshing state when permissions are being reloaded (e.g. location swap)
   */
  isRefreshingPermissions: boolean;
}

/**
 * Hook to access permission checking in components
 */
export function usePermissions(): UsePermissionsReturn {
  const auth = useAuth();

  return {
    user: auth.user,
    activeRole: auth.activeRole,
    hasPermission: auth.hasPermission,
    hasModulePermission: auth.hasModulePermission as any,
    hasResourcePermission: auth.hasResourcePermission as any,
    canAccessModule: auth.canAccessModule as any,
    getAccessibleModules: auth.getAccessibleModules as any,
    isLoadingPermissions: auth.isLoadingPermissions,
    isRefreshingPermissions: auth.isRefreshingPermissions,
  };
}

export default usePermissions;
